import ClientLayoutWithOutHeader from "../../client/components/layout/ClientLayoutWithOutHeaderForTeenPatti";
import TPStudioSidebar from "../../client/tp-studio/TPStudioSidebar";
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import TpStudioHeader from "../../client/tp-studio/tp-studio-header";
import TpStudioLoader from "../../client/tp-studio/tp-studio-loader";
import "../../../assets/topstudio/styles/Player.css";
import "../../../assets/topstudio/styles/FortuneGems.css";
import axiosInstance from "../../../utils/axiosInstance";
import useSocket from "../../../hooks/useSocket";
import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../../store/userSlice";
import FortuneGemsRules from "./pop-up/fortunegems-rules";

const FG = {
    bg: "#082d32",
    card: "#0c3b42",
    ink: "#f3e9cd",
    muted: "#b9b19a",
    grid: "#0f4a52",
    green: "#1d644e",
    amber: "#b97718",
    blue: "#0d3e53",
    red: "#8f2d1f",
    gold: "#e7c66a",
};

const SYMBOL_META = {
    1: { name: "J", color: "#e7c66a", accent: "#fff5cc", mult: 2 },
    2: { name: "Q", color: "#3aa9ff", accent: "#cfe9ff", mult: 3 },
    3: { name: "K", color: "#9b5bff", accent: "#e2d0ff", mult: 5 },
    4: { name: "A", color: "#ffb000", accent: "#fff1cc", mult: 8 },
    5: { name: "Green", color: "#3fbf6a", accent: "#c7f5d6", mult: 15 },
    6: { name: "Blue", color: "#3aa9ff", accent: "#cfe9ff", mult: 25 },
    7: { name: "Red", color: "#ff4d4d", accent: "#ffd0d0", mult: 50 },
};
const SYMBOL_IDS = [1, 2, 3, 4, 5, 6, 7];

const SYMBOL_IMG_SRC = {
    1: "/assets/fortune/j.png",
    2: "/assets/fortune/q.png",
    3: "/assets/fortune/k.png",
    4: "/assets/fortune/a.png",
    5: "/assets/fortune/green.png",
    6: "/assets/fortune/blue.png",
    7: "/assets/fortune/red.png",
};

const SYMBOL_NAME_TO_ID = {
    J: 1,
    Q: 2,
    K: 3,
    A: 4,
    GREEN: 5,
    BLUE: 6,
    RED: 7,
};

function symbolNameToId(value) {
    if (typeof value === "number") return value;
    if (value == null) return 0;
    const key = String(value).trim().toUpperCase();
    return SYMBOL_NAME_TO_ID[key] || 0;
}

function parseAcGrid(round) {
    if (!round) return null;
    const split = (s) =>
        String(s || "")
            .split(",")
            .map((x) => x.trim())
            .filter((x) => x.length > 0);
    const r1 = split(round.ac1);
    const r2 = split(round.ac2);
    const r3 = split(round.ac3);
    if (r1.length === 0 && r2.length === 0 && r3.length === 0) return null;

    const reels = [];
    for (let c = 0; c < 3; c++) {
        reels.push([
            symbolNameToId(r1[c]),
            symbolNameToId(r2[c]),
            symbolNameToId(r3[c]),
        ]);
    }
    return reels;
}

function evaluatePaylines(grid) {
    if (!Array.isArray(grid) || grid.length < 3) return [];
    const wins = [];
    for (const pl of PAYLINES) {
        const s0 = grid[0] && grid[0][pl.rows[0]];
        const s1 = grid[1] && grid[1][pl.rows[1]];
        const s2 = grid[2] && grid[2][pl.rows[2]];
        if (s0 && s0 === s1 && s1 === s2) {
            const meta = SYMBOL_META[s0];
            wins.push({ line: pl.line, symbol: s0, mult: meta ? meta.mult : 0 });
        }
    }
    return wins;
}
const BG_IMG_SRC = "/assets/fortune/fortune-gems-background.png";

const FRAME_INSET = { left: 0.205, top: 0.115, right: 0.205, bottom: 0.105 };

const DEFAULT_ROUND_SECONDS = 10;
const COUNTDOWN_RADIUS = 44;
const COUNTDOWN_CIRCUMFERENCE = Math.round(2 * Math.PI * COUNTDOWN_RADIUS);

const RESULT_DISPLAY_MS = 3500;

const PAYLINES = [
    { line: 1, rows: [0, 0, 0] },
    { line: 2, rows: [1, 1, 1] },
    { line: 3, rows: [2, 2, 2] },
    { line: 4, rows: [0, 1, 2] },
    { line: 5, rows: [2, 1, 0] },
];

const CHIP_VALUES = [500, 2000, 5000, 10000, 25000];

function randomStrip(n) {
    const strip = [];
    for (let i = 0; i < n; i++) {
        strip.push(SYMBOL_IDS[Math.floor(Math.random() * SYMBOL_IDS.length)]);
    }
    return strip;
}

const IDLE_PATTERN = [2, 3, 1];
const IDLE_STRIP_LEN = 30;

function buildIdleStrip() {
    const arr = new Array(IDLE_STRIP_LEN);
    for (let i = 0; i < IDLE_STRIP_LEN; i++) {
        arr[i] = IDLE_PATTERN[i % IDLE_PATTERN.length];
    }
    return arr;
}

function buildFinalStrip(finalCol) {
    const padding = 30;
    const arr = [];
    for (let i = 0; i < padding; i++) {
        arr.push(SYMBOL_IDS[Math.floor(Math.random() * SYMBOL_IDS.length)]);
    }
    arr.push(finalCol[0], finalCol[1], finalCol[2]);
    return arr;
}

function loadImageElement(img, src) {
    return new Promise((resolve) => {
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
        if (img.complete && img.naturalWidth > 0) resolve();
    });
}

function drawSymbolCell(ctx, sym, x, y, w, h, images) {
    const img = images && images[sym];
    if (img && img.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, x, y, w, h);
    }
}

export default function FortuneGems() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.data);

  const { data: socketData } = useSocket({
    marketId: null,
    mtype: "teenpatti",
    type: "fortunegems_virtual"
  });
  //console.log("Socket data:", socketData);

  const payload = socketData?.data?.data;
  // do not remove it. it is used for debugging.
  //console.log('payload: ', payload);
  if (payload?.ac1) {
    //console.log('ac: ', payload?.ac1);
    //console.log('ac2: ', payload?.ac2);
    //console.log('ac3: ', payload?.ac3);
    //console.log('-------');
  } else {
  }
  const payloadResult = socketData?.data?.result;
  const socketUser = socketData?.user;

  const [showRules, setShowRules] = useState(false);
  const [open, setOpen] = useState(false);
  const [displayLoader, setDisplayLoader] = useState(true);
  const [activeTabIndex, setActiveTabIndex] = useState(1);

  const [betAmount, setBetAmount] = useState(500);
  const [lines, setLines] = useState(1);
  const [autoSpin, setAutoSpin] = useState(false);

  const [roundId, setRoundId] = useState("");
  const [evtStatus, setEvtStatus] = useState("OPEN");
  const [countdown, setCountdown] = useState(DEFAULT_ROUND_SECONDS);
  const [spinning, setSpinning] = useState(false);

  const [betLoading, setBetLoading] = useState(false);
  const [betPlaced, setBetPlaced] = useState(false);
  const [currentBetId, setCurrentBetId] = useState(null);
  const [currentBetAmount, setCurrentBetAmount] = useState(0);
  const [lastWinMult, setLastWinMult] = useState(0);
  const [lastWinLines, setLastWinLines] = useState([]);
  const [profitLoss, setProfitLoss] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [shortResults, setShortResults] = useState([]);

  const roundIdRef = useRef("");
  const lastClosedRoundRef = useRef("");
  const betPlacedRef = useRef(false);
  const currentBetAmountRef = useRef(0);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(0);
  const flashTimerRef = useRef(null);
  const resultTimerRef = useRef(null);
  const imagesRef = useRef({ bg: null, symbols: {} });
  const canvasAssetsReadyRef = useRef(false);
  const [resultdata, setResultdata] = useState([]);
  const sessionUser = useSelector((s) => s.userStore.data);

  useEffect(() => {
    updateUserData(socketUser);
  }, [socketUser, dispatch]);

  useEffect(() => {
    stateRef.current = {
      reels: [
        {
          offset: 0,
          speed: 0,
          target: null,
          stopAt: 0,
          stopped: true,
          finalStrip: null
        },
        {
          offset: 0,
          speed: 0,
          target: null,
          stopAt: 0,
          stopped: true,
          finalStrip: null
        },
        {
          offset: 0,
          speed: 0,
          target: null,
          stopAt: 0,
          stopped: true,
          finalStrip: null
        }
      ],
      strips: [buildIdleStrip(), buildIdleStrip(), buildIdleStrip()],
      world: { width: 0, height: 0 },
      timePrev: 0,
      winLineFlash: 0,
      winLines: [],
      finalGrid: null,
      spinning: false,
      lastWinMult: 0,
      betAmount: 0
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const bg = new Image();
    const syms = {};
    const ids = Object.keys(SYMBOL_IMG_SRC);
    ids.forEach((id) => {
      syms[id] = new Image();
    });
    const tasks = [
      loadImageElement(bg, BG_IMG_SRC),
      ...ids.map((id) => loadImageElement(syms[id], SYMBOL_IMG_SRC[id]))
    ];
    Promise.all(tasks).then(() => {
      if (cancelled) return;
      imagesRef.current = { bg, symbols: syms };
      canvasAssetsReadyRef.current = true;
      setDisplayLoader(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const s = stateRef.current;
    if (!s) return;
    s.spinning = spinning;
    s.lastWinMult = lastWinMult;
    s.betAmount = Number(betAmount) || 0;
  }, [spinning, lastWinMult, betAmount]);

  useEffect(() => {
    currentBetAmountRef.current = currentBetAmount;
  }, [currentBetAmount]);

  useEffect(() => {
    betPlacedRef.current = betPlaced;
  }, [betPlaced]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const resize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight || Math.round((w * 720) / 1280);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const s = stateRef.current;
      if (s) {
        s.world.width = w;
        s.world.height = h;
      }
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    window.addEventListener("resize", resize);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const tick = (t) => {
      const s = stateRef.current;
      if (s) {
        const dt = Math.min(0.05, (t - (s.timePrev || t)) / 1000);
        s.timePrev = t;
        update(s, dt, t / 1000);
        draw(ctx, s, t / 1000);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const update = (s, dt, tSec) => {
    for (let c = 0; c < 3; c++) {
      const r = s.reels[c];
      if (r.stopped) continue;
      r.offset += r.speed * dt;
      if (r.stopAt > 0 && tSec >= r.stopAt) {
        r.offset = r.target;
        r.stopped = true;
        r.stopAt = 0;
        r.speed = 0;
      }
    }
  };

  const draw = (ctx, s, time) => {
    const W = s.world.width;
    const H = s.world.height;
    if (!W || !H) return;
    if (!canvasAssetsReadyRef.current) return;

    ctx.fillStyle = FG.bg;
    ctx.fillRect(0, 0, W, H);
    const bgImg = imagesRef.current.bg;
    if (bgImg && bgImg.complete && bgImg.naturalWidth > 0) {
      ctx.drawImage(bgImg, 0, 0, W, H);
    }

    const innerX = W * FRAME_INSET.left;
    const innerY = H * FRAME_INSET.top;
    const innerW = W - innerX - W * FRAME_INSET.right;
    const innerH = H - innerY - H * FRAME_INSET.bottom;
    const reelsAreaW = innerW;
    const reelsAreaH = innerH;
    const reelW = reelsAreaW / 3;
    const cellH = reelsAreaH / 3;
    const margin = innerX;
    const reelTop = innerY;

    for (let c = 0; c < 3; c++) {
      const r = s.reels[c];
      const x = margin + c * reelW;
      const y = reelTop;
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, reelW, reelsAreaH);
      ctx.clip();

      const strip = r.finalStrip || s.strips[c];
      const off = r.offset;
      const baseIdx = Math.floor(off);
      const frac = off - baseIdx;
      for (let row = -1; row < 3; row++) {
        const sIdx =
          (((baseIdx + row) % strip.length) + strip.length) % strip.length;
        const sym = strip[sIdx];
        const cy = y + (row - frac + 1) * cellH;
        drawSymbolCell(
          ctx,
          sym,
          x,
          cy,
          reelW,
          cellH,
          imagesRef.current.symbols
        );
      }
      ctx.restore();
    }

    if (s.winLineFlash > 0 && s.winLines && s.winLines.length) {
      const pulse = 0.55 + 0.45 * Math.sin(time * 6);
      for (const w of s.winLines) {
        const pl = PAYLINES.find((p) => p.line === w.line);
        if (!pl) continue;
        ctx.save();
        ctx.strokeStyle = `rgba(243,233,205,${pulse})`;
        ctx.lineWidth = 4;
        ctx.shadowColor = "rgba(231,198,106,0.9)";
        ctx.shadowBlur = 14;
        ctx.beginPath();
        for (let c = 0; c < 3; c++) {
          const cx = margin + c * reelW + reelW / 2;
          const cy = reelTop + (pl.rows[c] + 0.5) * cellH;
          if (c === 0) ctx.moveTo(cx, cy);
          else ctx.lineTo(cx, cy);
        }
        ctx.stroke();
        ctx.restore();
      }
    }
  };

  const beginRoundClose = useCallback(
    (cat_mid, reels, lineWins, totalMult) => {
      const s = stateRef.current;
      if (!s) return;

      setSpinning(true);

      if (Array.isArray(reels) && reels.length === 3) {
        const now = performance.now() / 1000;
        for (let c = 0; c < 3; c++) {
          const r = s.reels[c];
          const finalCol = reels[c];
          const strip = buildFinalStrip(finalCol);
          r.finalStrip = strip;
          r.target = strip.length - 2;
          r.stopAt = now + 1.0 + c * 0.5;
          r.stopped = false;
          r.offset = 0;
          r.speed = 18 + Math.random() * 4;
        }
      }

      const ms = (1.0 + 2 * 0.5) * 1000 + 50;
      if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
      resultTimerRef.current = setTimeout(() => {
        const ss = stateRef.current;
        if (!ss) return;
        ss.finalGrid = reels;
        ss.winLines = lineWins || [];

        const placedAmt = currentBetAmountRef.current;
        const userPlacedThisRound = betPlacedRef.current && placedAmt > 0;
        const win = totalMult * placedAmt;
        const profit = userPlacedThisRound
          ? totalMult > 0
            ? totalMult * placedAmt
            : -placedAmt
          : null;

        if (userPlacedThisRound) {
          setProfitLoss(profit);
        }

        if ((lineWins || []).length > 0 && totalMult > 0) {
          ss.winLineFlash = 1;
          setLastWinMult(totalMult);
          setLastWinLines(lineWins || []);
          if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
          flashTimerRef.current = setTimeout(() => {
            const sss = stateRef.current;
            if (sss) sss.winLineFlash = 0;
            flashTimerRef.current = null;
          }, RESULT_DISPLAY_MS);
        } else {
          setLastWinMult(0);
          setLastWinLines([]);
        }

        setShortResults((prev) => {
          const next = [{ id: cat_mid, mult: totalMult, win }, ...prev];
          return next.slice(0, 8);
        });
        setSpinning(false);
        resultTimerRef.current = null;
      }, ms);
    },
    [user, lines]
  );

  useEffect(() => {
    if (!payload) return;

    updateUserData(socketUser?.user);
    setDisplayLoader(false);

    const incomingId = payload.cat_mid != null ? String(payload.cat_mid) : "";
    if (!incomingId) return;
    const incomingStatus = String(payload.evt_status || "OPEN").toUpperCase();
    const serverLeft =
      typeof payload.left_time === "number" ? payload.left_time : null;

    const isNewRound = incomingId !== roundIdRef.current;

    if (isNewRound) {
      if (resultTimerRef.current) {
        clearTimeout(resultTimerRef.current);
        resultTimerRef.current = null;
      }
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
        flashTimerRef.current = null;
      }
      const s = stateRef.current;
      if (s) {
        s.winLineFlash = 0;
        s.winLines = [];
        s.finalGrid = null;
      }

      roundIdRef.current = incomingId;
      setRoundId(incomingId);
      setBetPlaced(false);
      setCurrentBetId(null);
      setCurrentBetAmount(0);
      setProfitLoss(null);
      setLastWinLines([]);
      setLastWinMult(0);
      setErrorMsg("");
    }

    setEvtStatus(incomingStatus);
    if (serverLeft != null) setCountdown(Math.max(0, Math.round(serverLeft)));

    const resolveRoundOutcome = () => {
      let reels =
        Array.isArray(payload.reels) && payload.reels.length === 3
          ? payload.reels
          : null;
      if (!reels) reels = parseAcGrid(payload);

      let lineWins = Array.isArray(payload.line_wins)
        ? payload.line_wins
        : null;
      if (!lineWins) lineWins = reels ? evaluatePaylines(reels) : [];

      let totalMult =
        typeof payload.totalMult === "number" ? payload.totalMult : null;
      if (totalMult == null) {
        totalMult = (lineWins || []).reduce(
          (a, w) => a + (Number(w.mult) || 0),
          0
        );
      }
      return { reels, lineWins, totalMult };
    };

    if (lastClosedRoundRef.current !== incomingId) {
      const { reels, lineWins, totalMult } = resolveRoundOutcome();
      if (Array.isArray(reels) && reels.length === 3) {
        lastClosedRoundRef.current = incomingId;
        beginRoundClose(incomingId, reels, lineWins, totalMult);
      }
    }
  }, [payload, beginRoundClose]);

  useEffect(() => {
    if (evtStatus !== "OPEN" || countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(t);
  }, [evtStatus, countdown]);

  const updateUserData = async (userData) => {
    //await dispatch(setUserData(userData));
    if (!userData || !sessionUser) return;
    await dispatch(
      setUserData({
        ...sessionUser,
        fname: userData.fname ?? sessionUser.fname,
        uname: userData.uname ?? sessionUser.uname,
        bz_balance: userData.bz_balance ?? sessionUser.bz_balance,
        opin_bal: userData.opin_bal ?? sessionUser.opin_bal,
        user_role: sessionUser.user_role,
        _id: sessionUser._id
      })
    );
  };

  const onSpinClick = useCallback(() => {
    if (spinning || betLoading || betPlaced) return;
    if (evtStatus !== "OPEN") return;

    const amt = Number(betAmount) || 0;
    if (amt <= 0) {
      setErrorMsg("Enter a valid bet amount.");
      return;
    }
    if (!roundId) {
      setErrorMsg("Round is not ready yet.");
      return;
    }
    const linesCount = Math.max(1, Math.min(5, Number(lines) || 1));

    setErrorMsg("");
    setLastWinMult(0);
    setLastWinLines([]);
    setCurrentBetAmount(amt);
    setBetLoading(true);

    const payloadBody = {
      catmid: roundId,
      teamname: "fortunegems",
      bettype: "b",
      odds: 1,
      amount: amt,
      game_type: 7,
      market_type: "m",
      game_name: "fortunegems",
      runnername: "Fortune Gems",
      bet_type: "b",
      bet_array: [
        {
          bet_amount: amt,
          section_no: 1,
          lines: linesCount
        }
      ]
    };

    axiosInstance
      .post("/fortune-gems/place-bet", payloadBody)
      .then((res) => {
        const data = (res && res.data && res.data.data) || {};
        if (!res?.data?.status) {
          setErrorMsg(res?.data?.message || "Bet failed.");
          setCurrentBetAmount(0);
          return;
        }
        setBetPlaced(true);
        const returnBets =
          Array.isArray(data.return_bets) && data.return_bets.length
            ? data.return_bets
            : null;
        const firstBetId = returnBets ? returnBets[0]?.bet_id : null;
        setCurrentBetId(firstBetId || data.bet_id || null);
      })
      .catch((err) => {
        console.error("fortune-gems/place-bet failed:", err);
        setErrorMsg(err?.response?.data?.message || "Bet failed.");
        setCurrentBetAmount(0);
      })
      .finally(() => setBetLoading(false));
  }, [betAmount, lines, spinning, betLoading, betPlaced, evtStatus, roundId]);

  useEffect(() => {
    if (!autoSpin) return;
    if (evtStatus !== "OPEN") return;
    if (betPlaced || betLoading || spinning) return;
    if (!roundId) return;
    const t = setTimeout(() => onSpinClick(), 250);
    return () => clearTimeout(t);
  }, [
    autoSpin,
    evtStatus,
    roundId,
    betPlaced,
    betLoading,
    spinning,
    onSpinClick
  ]);

  const stepBet = (dir) => {
    const idx = CHIP_VALUES.indexOf(Number(betAmount));
    if (idx === -1) {
      setBetAmount(CHIP_VALUES[0]);
      return;
    }
    const next = Math.max(0, Math.min(CHIP_VALUES.length - 1, idx + dir));
    setBetAmount(CHIP_VALUES[next]);
  };
  const stepLines = (dir) => {
    setLines((v) => Math.max(1, Math.min(5, v + dir)));
  };

  const shortResultClass = (mult) => {
    if (!mult || mult <= 0)
      return "video-button border-gradient-btn border-gradient-purple-btn fg-loss";
    if (mult >= 10)
      return "video-button border-gradient-btn border-gradient-purple-btn fg-big";
    return "video-button border-gradient-btn border-gradient-purple-btn fg-win";
  };

  const allBets = useMemo(() => {
    const list = Array.isArray(payload?.lucky_players)
      ? payload.lucky_players
      : [];
    return list.map((b, i) => ({
      id: b._id || `lp-${i}`,
      username: b.uname || "***",
      lines: Number(b.lines) || 1,
      bet: Number(b.amount) || 0,
      profit:
        b.profit == null || b.profit === undefined ? null : Number(b.profit)
    }));
  }, [payload?.lucky_players]);

  const recentResults = useMemo(() => {
    if (shortResults.length > 0) return shortResults;
    if (!payloadResult) return [];
    const list = Array.isArray(payloadResult)
      ? payloadResult
      : Object.keys(payloadResult).map((k) => payloadResult[k]);
    return list
      .map((entry, i) => {
        if (entry && typeof entry === "object") {
          const id = entry.id || entry.cat_mid || entry.round_id || `r-${i}`;
          const mult = Number(
            entry.mult ?? entry.totalMult ?? entry.total_mult ?? 0
          );
          return { id, mult, win: 0 };
        }
        const m = Number(String(entry).replace(/[^\d.\-]/g, "")) || 0;
        return { id: `r-${i}`, mult: m, win: 0 };
      })
      .slice(0, 8);
  }, [shortResults, payloadResult]);

  const resultData = useMemo(() => {
    if (!payloadResult) return [];
    const list = Array.isArray(payloadResult)
      ? payloadResult
      : Object.keys(payloadResult).map((k) => payloadResult[k]);
    return list.map((entry, i) => {
      if (!entry || typeof entry !== "object") {
        const m = Number(String(entry).replace(/[^\d.\-]/g, "")) || 0;
        return {
          id: `r-${i}`,
          lines: [],
          totalMult: m
        };
      }
      return {
        id: entry.id || entry.cat_mid || entry.round_id || `r-${i}`,
        lines: Array.isArray(entry.lines)
          ? entry.lines
          : Array.isArray(entry.line_wins)
          ? entry.line_wins
          : [],
        totalMult: Number(
          entry.totalMult ?? entry.total_mult ?? entry.mult ?? 0
        )
      };
    });
  }, [payloadResult]);

  const showResultDetail = async () => {
    let resultObj = {};
    resultObj.game_type = "fortunegems_virtual";

    //console.log("BEFORE API CALL");

    const response = await axiosInstance.post("/teenpatti-results", resultObj);
    //console.log("FULL RESPONSE", response.data);
    //console.log("AFTER API CALL");

    setResultdata(response.data.results_list);
    //console.log("response->", resultdata);
  };

  const activeTab = (tab1) => {
    setActiveTabIndex(tab1);
    if (tab1 == 2) {
      showResultDetail();
    }
  };

  return (
    <>
      <div className="aviator-scope player-scope aviator-bg-black">
        <ClientLayoutWithOutHeader showLoader={false}>
          <div className="details-tabs-wrapper1">
            <div className="main-page">
              <div className="row no-gutters">
                <div className="col-md-12">
                  <div className="bodyheight">
                    <div className="wrapper">
                      <TPStudioSidebar open={open} />
                    </div>
                    <div className="lobby-ctn BPGmaindiv">
                      <TpStudioHeader setOpen={setOpen} open={open} />
                      <TpStudioLoader displayLoader={displayLoader} />
                    </div>

                    <div
                      className="row"
                      style={{ display: displayLoader ? "none" : "" }}
                    >
                      <div className="col-md-8 Exgamesbg">
                        <div className="left-content">
                          <div className="footer-divbpg border-gradient-footer border-gradient-purple-footer h-45">
                            <div className="col-6 col-md-6 titlebarname inline-block Gameidcol mt-2">
                              <p>Fortune Gems</p>
                            </div>
                            <div
                              className="col-6 col-md-6 inline-block Gameidcol text-right"
                              style={{ marginTop: "4px" }}
                            >
                              <div
                                role="group"
                                aria-label="tabs"
                                className="btn-group btn-group-sm mr-2"
                              >
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  className={
                                    activeTabIndex === 1
                                      ? "tablink btn btn-primary bbactive"
                                      : "tablink btn btn-primary"
                                  }
                                  onClick={() => activeTab(1)}
                                >
                                  Game
                                </button>
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  className={
                                    activeTabIndex === 2
                                      ? "tablink btn btn-primary bbactive"
                                      : "tablink btn btn-primary"
                                  }
                                  onClick={() => activeTab(2)}
                                >
                                  Results
                                </button>
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  className="tablink btn btn-primary"
                                  onClick={() => setShowRules(true)}
                                >
                                  Rules
                                </button>
                              </div>
                            </div>
                          </div>

                          <FortuneGemsRules
                            show={showRules}
                            onClose={() => setShowRules(false)}
                          />

                          {activeTabIndex === 1 && (
                            <>
                              <div className="cards-layout blackjack">
                                <div
                                  className="FGstage"
                                  id="FGstage"
                                  ref={containerRef}
                                >
                                  <canvas id="FGcanvas" ref={canvasRef} />
                                  <div
                                    id="FGerr"
                                    className={
                                      "FGerror" +
                                      (errorMsg ? " is-visible" : "")
                                    }
                                  >
                                    {errorMsg}
                                  </div>

                                  <div className="FGactions">
                                    <button
                                      type="button"
                                      className={
                                        "FGroundBtn FGroundBtn--auto" +
                                        (autoSpin ? " is-on" : "")
                                      }
                                      onClick={() => setAutoSpin((v) => !v)}
                                      disabled={betLoading}
                                      title="Auto spin"
                                      aria-pressed={autoSpin}
                                    >
                                      <span className="FGautoLabel">AUTO</span>
                                      {autoSpin ? (
                                        <span
                                          className="FGautoTick"
                                          aria-hidden
                                        >
                                          <svg
                                            viewBox="0 0 48 48"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M10 25 L21 36 L38 12"
                                              stroke="currentColor"
                                              strokeWidth="8"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        </span>
                                      ) : null}
                                    </button>
                                    <div className="FGbetButtonWrapper">
                                      <button
                                        type="button"
                                        className="FGroundBtn FGroundBtn--bet"
                                        onClick={onSpinClick}
                                        disabled={
                                          spinning ||
                                          betLoading ||
                                          betPlaced ||
                                          evtStatus !== "OPEN"
                                        }
                                        title="Spin"
                                      >
                                        {betLoading ? "…" : "BET"}
                                      </button>
                                      <div
                                        className={`FGbetLock ${
                                          evtStatus !== "OPEN" || betPlaced
                                            ? "is-active"
                                            : ""
                                        }`}
                                      >
                                        ✕
                                      </div>
                                    </div>
                                  </div>

                                  <div className="FGbottomBar">
                                    <div className="FGpill">
                                      <button
                                        type="button"
                                        className="FGstep"
                                        onClick={() => stepBet(-1)}
                                        disabled={spinning}
                                        aria-label="Decrease bet"
                                      >
                                        −
                                      </button>
                                      <div className="FGpillBody">
                                        <span className="FGpillLabel">Bet</span>
                                        <span className="FGpillValue">
                                          {Number(betAmount).toLocaleString()}
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        className="FGstep"
                                        onClick={() => stepBet(1)}
                                        disabled={spinning}
                                        aria-label="Increase bet"
                                      >
                                        +
                                      </button>
                                    </div>
                                    <div className="FGpill">
                                      <button
                                        type="button"
                                        className="FGstep"
                                        onClick={() => stepLines(-1)}
                                        disabled={spinning}
                                        aria-label="Decrease lines"
                                      >
                                        −
                                      </button>
                                      <div className="FGpillBody">
                                        <span className="FGpillLabel">
                                          Lines
                                        </span>
                                        <span className="FGpillValue">
                                          {lines}
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        className="FGstep"
                                        onClick={() => stepLines(1)}
                                        disabled={spinning}
                                        aria-label="Increase lines"
                                      >
                                        +
                                      </button>
                                    </div>
                                  </div>

                                  <div className="FGbadge">
                                    Round: {roundId || "—"}
                                  </div>

                                  {profitLoss !== null && !spinning && (
                                    <div className="FGresultCenter">
                                      <img
                                        src="/assets/fortune/flower.png"
                                        alt="Result flower"
                                        className="FGresultFlower"
                                      />
                                      <div
                                        className={`FGresultAmount ${
                                          profitLoss < 0
                                            ? "FGresultAmount--loss"
                                            : ""
                                        }`}
                                      >
                                        {profitLoss.toLocaleString()}
                                      </div>
                                    </div>
                                  )}

                                  {evtStatus === "OPEN" && countdown > 0 && (
                                    <div className="FGcountdown">
                                      <svg
                                        viewBox="0 0 100 100"
                                        className="FGcountdownSvg"
                                      >
                                        <g className="FGcountdownCircle">
                                          <circle
                                            cx="50"
                                            cy="50"
                                            r="44"
                                            className="FGcountdownPathElapsed"
                                          />
                                          <path
                                            className="FGcountdownPathRemaining"
                                            strokeDasharray={`${COUNTDOWN_CIRCUMFERENCE}`}
                                            strokeDashoffset={`${Math.round(
                                              COUNTDOWN_CIRCUMFERENCE *
                                                (1 -
                                                  Math.min(
                                                    1,
                                                    countdown /
                                                      DEFAULT_ROUND_SECONDS
                                                  ))
                                            )}`}
                                            d="M 50, 50 m -44, 0 a 44,44 0 1,0 88,0 a 44,44 0 1,0 -88,0"
                                          />
                                        </g>
                                      </svg>
                                      <span className="FGcountdownLabel">
                                        {countdown}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div
                                className="buttons-div"
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: 6
                                }}
                              >
                                {recentResults.length === 0 ? (
                                  <span
                                    style={{ color: FG.muted, fontSize: 12 }}
                                  >
                                    Recent spins will appear here
                                  </span>
                                ) : (
                                  recentResults.map((r, i) => (
                                    <button
                                      key={`${r.id}-${i}`}
                                      type="button"
                                      className={shortResultClass(r.mult)}
                                      title={String(r.id)}
                                    >
                                      {r.mult > 0 ? `${r.mult}x` : "—"}
                                    </button>
                                  ))
                                )}
                              </div>
                            </>
                          )}

                          {activeTabIndex === 2 && (
                            <div className="SLT_RES_data_container">
                              {resultdata.length === 0 ? (
                                <div className="SLT_RES_message_tile">
                                  No results to display
                                </div>
                              ) : (
                                resultdata.map((r, idx) => {
                                  const wins = Array.isArray(r.lines)
                                    ? r.lines
                                    : [];
                                  return (
                                    <div
                                      key={`${r.id}-${idx}`}
                                      className="SLT_RES_round_card"
                                    >
                                      <div className="SLT_RES_round_id">
                                        Round: <span>{r.id}</span>
                                      </div>
                                      <div className="SLT_RES_line_data">
                                        {wins.length === 0 ? (
                                          <div className="SLT_RES_message_tile">
                                            No win
                                          </div>
                                        ) : (
                                          <div className="SLT_RES_line_grid">
                                            {wins.map((l, i) => (
                                              <div
                                                key={i}
                                                className="SLT_RES_line_tile"
                                              >
                                                <div className="SLT_RES_line_label">
                                                  Line {l.line}
                                                </div>
                                                <div className="SLT_RES_line_mult">
                                                  {l.mult}x
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-4 mobile-margin">
                        <div>
                          <div className="bets">
                            <strong>Lucky Players {allBets.length}</strong>
                            <div className="lucky-players-container">
                              <div className="betting-table">
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th>User</th>
                                      <th>Size</th>
                                      <th>Price</th>
                                      <th>Profit</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {allBets.length === 0 ? (
                                      <tr>
                                        <td
                                          colSpan={4}
                                          style={{
                                            textAlign: "center",
                                            opacity: 0.7
                                          }}
                                        >
                                          No players yet
                                        </td>
                                      </tr>
                                    ) : (
                                      allBets.map((bet, idx) => (
                                        <tr key={bet.id || idx}>
                                          <td>{bet.username}</td>
                                          <td>{bet.lines}</td>
                                          <td>
                                            {bet.bet > 0
                                              ? bet.bet.toLocaleString()
                                              : "—"}
                                          </td>
                                          <td
                                            style={{
                                              color:
                                                bet.profit < 0
                                                  ? "#ff6b6b"
                                                  : bet.profit > 0
                                                  ? "#7be07b"
                                                  : "#ffffff"
                                            }}
                                          >
                                            {bet.profit != null
                                              ? bet.profit.toLocaleString()
                                              : "—"}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ClientLayoutWithOutHeader>
      </div>
    </>
  );
}
